import type {
  AddTeamMemberPayload,
  CreateTeamPayload,
  Team,
  TeamListItem,
  TeamListResponse,
  TeamMember,
  TeamMemberListResponse,
  TeamResponse,
  UpdateTeamRequest,
  UpdateTeamRoleRequest,
} from '~/types';

import { apiRequest } from './client';
import { ENDPOINTS, HTTP_METHODS } from './config';

/**
 * Add a member to a team.
 *
 * @param teamId - Team ID
 * @param data - Member data (user_id and role)
 * @returns Created team member
 * @throws ApiError with status 400 if user already in team or not org member
 * @throws ApiError with status 403 if user lacks team admin permission
 * @throws ApiError with status 404 if team or user not found
 */
export async function addTeamMember(
  teamId: string,
  data: AddTeamMemberPayload,
): Promise<TeamMember> {
  const response = await apiRequest<TeamMember>(`${ENDPOINTS.TEAMS}/${teamId}/members`, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(data),
  });

  return response.data;
}

/**
 * Create a new team in an organization.
 *
 * @param orgId - Organization ID
 * @param data - Team creation data
 * @returns Created team response
 * @throws ApiError with status 409 if slug already exists
 * @throws ApiError with status 403 if user lacks permission
 */
export async function createTeam(orgId: string, data: CreateTeamPayload): Promise<TeamResponse> {
  const response = await apiRequest<TeamResponse>(`${ENDPOINTS.ORGANIZATIONS}/${orgId}/teams`, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(data),
  });

  return response.data;
}

/**
 * Delete a team permanently.
 *
 * @param teamId - Team ID
 * @throws ApiError with status 400 if team is default team
 * @throws ApiError with status 404 if team not found
 * @throws ApiError with status 403 if user lacks permission
 */
export async function deleteTeam(teamId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.TEAMS}/${teamId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Get all teams for an organization.
 *
 * @param orgId - Organization ID
 * @returns List of teams
 */
export async function getOrganizationTeams(orgId: string): Promise<TeamListItem[]> {
  const response = await apiRequest<TeamListResponse>(`${ENDPOINTS.ORGANIZATIONS}/${orgId}/teams`);
  return response.data.teams;
}

/**
 * Get team details including members.
 *
 * @param teamId - Team ID
 * @returns Team details with members
 * @throws ApiError with status 404 if team not found
 * @throws ApiError with status 403 if user lacks permission
 */
export async function getTeamDetail(teamId: string): Promise<Team> {
  const response = await apiRequest<Team>(`${ENDPOINTS.TEAMS}/${teamId}`);

  return response.data;
}

// =============================================================================
// Team Members API (Story 5.6)
// =============================================================================

/**
 * Get team members list with pagination.
 *
 * @param teamId - Team ID
 * @returns Paginated list of team members
 * @throws ApiError with status 403 if user lacks permission
 * @throws ApiError with status 404 if team not found
 */
export async function getTeamMembers(teamId: string): Promise<TeamMemberListResponse> {
  const response = await apiRequest<TeamMemberListResponse>(`${ENDPOINTS.TEAMS}/${teamId}/members`);

  return response.data;
}

/**
 * Remove a member from a team.
 *
 * @param teamId - Team ID
 * @param userId - User ID of member to remove
 * @throws ApiError with status 400 if removing last team admin
 * @throws ApiError with status 403 if user lacks team admin permission
 * @throws ApiError with status 404 if team or member not found
 */
export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.TEAMS}/${teamId}/members/${userId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Update a team's details.
 *
 * @param teamId - Team ID
 * @param data - Fields to update (name, description)
 * @returns Updated team response
 * @throws ApiError with status 404 if team not found
 * @throws ApiError with status 403 if user lacks team admin permission
 */
export async function updateTeam(teamId: string, data: UpdateTeamRequest): Promise<TeamResponse> {
  const response = await apiRequest<TeamResponse>(`${ENDPOINTS.TEAMS}/${teamId}`, {
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify(data),
  });

  return response.data;
}

/**
 * Update a team member's role.
 *
 * @param teamId - Team ID
 * @param userId - User ID of member to update
 * @param data - New role
 * @returns Updated team member
 * @throws ApiError with status 400 if demoting last team admin
 * @throws ApiError with status 403 if user lacks team admin permission
 * @throws ApiError with status 404 if team or member not found
 */
export async function updateTeamMemberRole(
  teamId: string,
  userId: string,
  data: UpdateTeamRoleRequest,
): Promise<TeamMember> {
  const response = await apiRequest<TeamMember>(`${ENDPOINTS.TEAMS}/${teamId}/members/${userId}`, {
    method: HTTP_METHODS.PATCH,
    body: JSON.stringify(data),
  });

  return response.data;
}
